// Invite welcome / accept page — task 104d.1 D1.6 (mockup) + 104d.2 D2.5
// (live wiring).
//
// Public URL: /invites/accept?token=...
//
// Two flows:
//   1. Not signed in: Clerk `<SignUp>` is embedded with the invite email
//      pre-filled and the email input locked (readonly). When `useUser()`
//      reports `isSignedIn=true` we POST the token to
//      `/api/v1/invites/accept` — Clerk hands us a session as soon as
//      sign-up finishes, so the `useEffect` below is the completion hook.
//   2. Signed in: "Continue as {email}" button calls the same accept
//      mutation directly. Email mismatch → "Sign out and try again" state.
//
// Preview payload:
//   - Mockup mode: static from `mockInvitePreview(token)`.
//   - Live mode: fetched via `useInvitePreview(token)` on mount.
//   - Error states (410 expired / revoked, 409 accepted, 403 mismatch) are
//     discriminated by the `status` field from the preview (pending /
//     expired / revoked / accepted) plus the signed-in email check. The
//     accept mutation surfaces 403 / 409 / 410 as error messages which we
//     then render through the same UI states as the mockup.
//
// This page lives OUTSIDE AppLayout (no sidebar / topbar) because it is a
// landing page, not an app page. Copy follows the "professional's
// assistant" framing — we greet, we don't surveil.

import { useEffect, useMemo, useRef } from 'react';
import { SignUp, SignOutButton, useUser } from '@clerk/clerk-react';
import { useSearchParams } from 'react-router-dom';
import { KanjoyWordmark } from '@/components/primitives/KanjoyWordmark';
import { Button } from '@/components/primitives/Button';
import { Badge } from '@/components/primitives/Badge';
import { config } from '@/config';
import { formatDayFull } from '@/lib/dates';
import { mockInvitePreview, type InvitePreview } from '@/data/invitePreview';
import { useInvitePreview } from '@/hooks/useInvitePreview';
import { useAcceptInvite } from '@/hooks/useAcceptInvite';
import type { KanjoyUserRole } from '@/data/kanjoyUsers';
import type { BadgeVariant } from '@/types/badge';
import styles from './InviteAcceptPage.module.css';

const ROLE_LABEL: Record<KanjoyUserRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
};

const ROLE_BADGE_VARIANT: Record<KanjoyUserRole, BadgeVariant> = {
  owner: 'owner',
  admin: 'admin',
  member: 'member',
};

export function InviteAcceptPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const showMismatch = params.get('mismatch') === '1';

  if (config.mockupMode) {
    return <MockupInviteAcceptBody token={token} showMismatch={showMismatch} />;
  }
  return <LiveInviteAcceptBody token={token} />;
}

// ── Mockup-mode body (unchanged behaviour from 104d.1) ──────────────────

function MockupInviteAcceptBody({
  token,
  showMismatch,
}: {
  token: string | null;
  showMismatch: boolean;
}) {
  const preview = useMemo(() => mockInvitePreview(token), [token]);
  return (
    <PageFrame>
      {token === null ? (
        <InviteNotFound />
      ) : preview.status !== 'pending' ? (
        <InviteNonPending status={preview.status} />
      ) : (
        <ActiveInvite preview={preview}>
          <MockupSignUpPlaceholder email={preview.email} showMismatch={showMismatch} />
        </ActiveInvite>
      )}
    </PageFrame>
  );
}

// ── Live-mode body (104d.2) ─────────────────────────────────────────────

function LiveInviteAcceptBody({ token }: { token: string | null }) {
  const { preview, isLoading, isNotFound } = useInvitePreview(token);

  if (token === null) {
    return (
      <PageFrame>
        <InviteNotFound />
      </PageFrame>
    );
  }
  if (isLoading) {
    return (
      <PageFrame>
        <p className={styles.lede}>Loading invite…</p>
      </PageFrame>
    );
  }
  if (isNotFound || !preview) {
    return (
      <PageFrame>
        <InviteNotFound />
      </PageFrame>
    );
  }
  if (preview.status !== 'pending') {
    return (
      <PageFrame>
        <InviteNonPending status={preview.status} />
      </PageFrame>
    );
  }
  return (
    <PageFrame>
      <ActiveInvite preview={preview}>
        <ClerkAcceptActions preview={preview} token={token} />
      </ActiveInvite>
    </PageFrame>
  );
}

// ── Shared layout frame ─────────────────────────────────────────────────

function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className={styles.page}>
      <main className={styles.card}>
        <header className={styles.header}>
          <KanjoyWordmark className={styles.wordmark} variant="dark" />
        </header>
        {children}
      </main>
    </div>
  );
}

// ── Active-invite body (happy-path payload) ──────────────────────────────

function ActiveInvite({
  preview,
  children,
}: {
  preview: InvitePreview;
  children: React.ReactNode;
}) {
  return (
    <>
      <h1 className={styles.title}>
        You{'’'}ve been invited to <span className={styles.orgName}>{preview.orgName}</span>
      </h1>
      <p className={styles.lede}>
        <strong>{preview.inviterName}</strong> invited you to join as{' '}
        <Badge variant={ROLE_BADGE_VARIANT[preview.role]}>{ROLE_LABEL[preview.role]}</Badge>. Kanjoy
        keeps your billable time in order — so you can focus on the work itself.
      </p>
      <dl className={styles.meta}>
        <div className={styles.metaRow}>
          <dt>Email</dt>
          <dd className={styles.metaEmail}>{preview.email}</dd>
        </div>
        <div className={styles.metaRow}>
          <dt>Expires</dt>
          <dd>{formatDayFull(preview.expiresAt)}</dd>
        </div>
      </dl>
      {children}
    </>
  );
}

// ── Mockup-mode placeholder ─────────────────────────────────────────────

function MockupSignUpPlaceholder({
  email,
  showMismatch,
}: {
  email: string;
  showMismatch: boolean;
}) {
  if (showMismatch) {
    return (
      <div className={styles.mismatchBlock}>
        <p className={styles.mismatchText}>
          You{'’'}re signed in as a different user. Sign out and try the invite link again.
        </p>
        <Button
          variant="secondary"
          onClick={() => {
            // no-op in mockup mode
          }}
        >
          Sign out and try again
        </Button>
      </div>
    );
  }
  return (
    <div className={styles.signupBlock}>
      <p className={styles.signupHint}>
        Create your Kanjoy account to accept. Your email is set to{' '}
        <strong className={styles.metaEmail}>{email}</strong> and can{'’'}t be changed here — the
        invite is specifically for that address.
      </p>
      <div
        className={styles.signupPlaceholder}
        role="group"
        aria-label="Sign-up form (live in 104d.2)"
      >
        <p className={styles.placeholderLine}>Sign-up form appears here in live mode.</p>
        <Button variant="primary" disabled>
          Accept invite
        </Button>
      </div>
    </div>
  );
}

// ── Live-mode Clerk accept actions (104d.2 D2.5) ────────────────────────

function ClerkAcceptActions({ preview, token }: { preview: InvitePreview; token: string }) {
  const { isSignedIn, user, isLoaded } = useUser();
  const accept = useAcceptInvite();
  // Guard the post-sign-up auto-accept so we only fire once per session:
  // Clerk's `isSignedIn` flips exactly once on sign-up completion, but
  // React may re-run the effect if the component remounts (e.g. Strict
  // Mode) or if `user` changes shape. A ref is the simplest one-time-fire
  // primitive here — a `useState` would force an extra render.
  const autoFiredRef = useRef(false);

  useEffect(() => {
    if (!isSignedIn || !user) return;
    if (autoFiredRef.current) return;
    const signedInEmail = user.primaryEmailAddress?.emailAddress?.toLowerCase() ?? '';
    // Only auto-accept when the signed-in email matches the invite email.
    // Mismatch path renders a sign-out button below — we do NOT auto-fire
    // there because the backend would 403 and we'd discard a fresh session.
    if (signedInEmail !== preview.email.toLowerCase()) return;
    autoFiredRef.current = true;
    accept.mutate(token, {
      onSuccess: () => {
        // Hard navigation — lands on `/` which redirects to /dashboard.
        // Soft navigation via useNavigate would stay in the React tree
        // with a stale AppContext until ClerkSync's effects resettle; a
        // top-level location change keeps the first post-accept render
        // authoritative.
        window.location.assign('/');
      },
    });
  }, [isSignedIn, user, preview.email, token, accept]);

  if (!isLoaded) return <p className={styles.signupHint}>Loading…</p>;

  if (isSignedIn && user) {
    const signedInEmail = user.primaryEmailAddress?.emailAddress ?? '';
    const matches = signedInEmail.toLowerCase() === preview.email.toLowerCase();
    if (!matches) {
      return (
        <div className={styles.mismatchBlock}>
          <p className={styles.mismatchText}>
            This invite was sent to <strong>{preview.email}</strong>, but you{'’'}re signed in as{' '}
            <strong>{signedInEmail}</strong>. Sign out and try the invite link again.
          </p>
          <SignOutButton>
            <Button variant="secondary">Sign out and try again</Button>
          </SignOutButton>
        </div>
      );
    }
    // The auto-accept effect above handles the happy path. Render a
    // button as a belt-and-suspenders manual trigger so the page isn't
    // "stuck" if the effect somehow doesn't fire — e.g. user hits the
    // back button mid-flight. The mutation is idempotent on the server
    // (409 INVITE_ALREADY_ACCEPTED after the first success), so the
    // extra click is safe.
    const errorMessage = (accept.error as Error | undefined)?.message;
    return (
      <div className={styles.continueBlock}>
        <Button
          variant="primary"
          disabled={accept.isPending || accept.isSuccess}
          onClick={() => {
            accept.mutate(token, {
              onSuccess: () => {
                window.location.assign('/');
              },
            });
          }}
        >
          {accept.isSuccess
            ? 'Accepted — redirecting…'
            : accept.isPending
              ? 'Accepting…'
              : `Continue as ${signedInEmail}`}
        </Button>
        {errorMessage ? (
          <p className={styles.mismatchText} role="alert">
            {errorMessage}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className={styles.signupBlock}>
      <p className={styles.signupHint}>
        Create your Kanjoy account to accept. Your email is locked to the invite address.
      </p>
      <SignUp
        initialValues={{ emailAddress: preview.email }}
        appearance={{
          elements: {
            // Lock the email input so the invitee can't retarget the
            // invite to a different address. See Clerk docs on
            // appearance.elements overrides.
            formFieldInput__emailAddress: { pointerEvents: 'none' },
          },
        }}
      />
    </div>
  );
}

// ── Error / not-found variants ──────────────────────────────────────────

function InviteNotFound() {
  return (
    <>
      <h1 className={styles.title}>Invite not found</h1>
      <p className={styles.lede}>
        This invite link is missing a token. Check the email you received for the full link, or ask
        your admin to send a new invite.
      </p>
    </>
  );
}

function InviteNonPending({ status }: { status: 'expired' | 'revoked' | 'accepted' }) {
  const { heading, body } = MESSAGES[status];
  return (
    <>
      <h1 className={styles.title}>{heading}</h1>
      <p className={styles.lede}>{body}</p>
    </>
  );
}

const MESSAGES: Record<'expired' | 'revoked' | 'accepted', { heading: string; body: string }> = {
  expired: {
    heading: 'This invite has expired',
    body: 'Invite links expire after seven days. Ask your admin to send a new one.',
  },
  revoked: {
    heading: 'This invite was revoked',
    body: 'Your admin revoked this invite. Ask them to send a new one if you still need access.',
  },
  accepted: {
    heading: 'This invite has already been accepted',
    body: 'If that was you, sign in to continue. If not, ask your admin to send a new invite.',
  },
};
